import React, { useState, useEffect, useCallback } from 'react';
import { reportsApi } from '../../../services/financeApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import CustomSelect from '../../Common/CustomSelect';
import CommonListPagination from '../../Common/CommonListPagination';
import {
  ArrowPathIcon,
  ArrowDownTrayIcon,
  ClipboardIcon,
  CheckIcon,
  ComputerDesktopIcon,
  MapPinIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const AuditLogsViewer = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [method, setMethod] = useState('');
  const [statusCodeGroup, setStatusCodeGroup] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [summary, setSummary] = useState({
    total: 0,
    methods: { POST: 0, PUT: 0, DELETE: 0, PATCH: 0 },
    statusCodes: { success: 0, error: 0 },
    uniqueUsersCount: 0,
  });

  const [selectedLog, setSelectedLog] = useState(null);
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchAuditLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await reportsApi.getAuditLogs({
        page,
        limit,
        search: debouncedSearch || undefined,
        method: method || undefined,
        statusCodeGroup: statusCodeGroup || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      if (response.data?.success) {
        const fetchedLogs = response.data.data.logs || [];
        const pagination = response.data.data.pagination || {};
        const fetchedSummary = response.data.data.summary || {};

        setLogs(fetchedLogs);
        setTotalPages(pagination.totalPages || 1);
        setTotalCount(pagination.totalCount || 0);
        setSummary(fetchedSummary);
      } else {
        throw new Error(response.data?.message || 'Failed to fetch audit logs');
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError(err.message || 'Something went wrong while fetching audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch, method, statusCodeGroup, startDate, endDate]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const handleResetFilters = () => {
    setSearch('');
    setMethod('');
    setStatusCodeGroup('');
    setStartDate('');
    setEndDate('');
    setPage(1);
    toast.success('Filters cleared successfully');
  };

  const formatTimestamp = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '—';

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
  };

  const handleCopyPayload = (payload) => {
    if (!payload) return;
    const text = typeof payload === 'object' ? JSON.stringify(payload, null, 2) : String(payload);
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Payload copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExport = async (format) => {
    if (exporting) return;
    setExporting(true);
    const loadToast = toast.loading(`Generating ${format.toUpperCase()} report...`);
    try {
      const response = await reportsApi.exportAuditLogs({
        format,
        search: search || undefined,
        method: method || undefined,
        statusCodeGroup: statusCodeGroup || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      const blob = new Blob([response.data], {
        type: format === 'csv' ? 'text/csv' : 'application/pdf',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `finance_audit_logs_${new Date().toISOString().slice(0, 10)}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      toast.success(`${format.toUpperCase()} report exported successfully!`, { id: loadToast });
    } catch (err) {
      console.error('Export failed:', err);
      toast.error(`Failed to export ${format.toUpperCase()} report`, { id: loadToast });
    } finally {
      setExporting(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((part) => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  const parseUserAgent = (ua) => {
    if (!ua) return { browser: 'Unknown Browser', os: 'Unknown OS' };

    let browser = 'Other Browser';
    let os = 'Other OS';

    if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Chrome') && !ua.includes('Chromium') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';

    if (ua.includes('Windows')) os = 'Windows OS';
    else if (ua.includes('Macintosh') || ua.includes('Mac OS')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux OS';
    else if (ua.includes('Android')) os = 'Android OS';
    else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS Device';

    return { browser, os };
  };

  return (
    <div>
      <div className="max-w-7xl mx-auto bg-gray-50">
        <div className="bg-white shadow-sm border border-gray-200">
          <div className="px-4 sm:px-6 py-2 border-b border-gray-200 flex-shrink-0">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search email, IP, or path..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="block w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-md bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                />
              </div>

              <div className="w-36">
                <CustomSelect
                  id="finance-audit-method"
                  value={method}
                  onChange={(e) => { setMethod(e.target.value); setPage(1); }}
                  options={[
                    { value: '', label: 'All Methods' },
                    { value: 'GET', label: 'GET (Exports)' },
                    { value: 'POST', label: 'POST' },
                    { value: 'PUT', label: 'PUT' },
                    { value: 'DELETE', label: 'DELETE' },
                    { value: 'PATCH', label: 'PATCH' },
                  ]}
                  size="sm"
                  variant="minimal"
                />
              </div>

              <div className="w-36">
                <CustomSelect
                  id="finance-audit-status"
                  value={statusCodeGroup}
                  onChange={(e) => { setStatusCodeGroup(e.target.value); setPage(1); }}
                  options={[
                    { value: '', label: 'All Statuses' },
                    { value: 'success', label: 'Success (2xx/3xx)' },
                    { value: 'client_error', label: 'Client Error (4xx)' },
                    { value: 'server_error', label: 'Server Error (5xx)' },
                  ]}
                  size="sm"
                  variant="minimal"
                />
              </div>

              <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-100 border border-gray-200">
                  <span className="text-[10px] font-medium text-gray-500">Total</span>
                  <span className="text-xs font-bold text-gray-800">{summary.total || 0}</span>
                </div>
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-green-50 border border-green-200">
                  <span className="text-[10px] font-medium text-green-700">Success</span>
                  <span className="text-xs font-bold text-green-800">{summary.statusCodes?.success || 0}</span>
                </div>
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-50 border border-red-200">
                  <span className="text-[10px] font-medium text-red-700">Errors</span>
                  <span className="text-xs font-bold text-red-800">{summary.statusCodes?.error || 0}</span>
                </div>

                <button
                  onClick={() => handleExport('csv')}
                  disabled={exporting || loading}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-[10px] font-semibold shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  title="Export to CSV"
                >
                  <ArrowDownTrayIcon className="w-3 h-3" />
                  CSV
                </button>
                <button
                  onClick={() => handleExport('pdf')}
                  disabled={exporting || loading}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-[10px] font-semibold shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  title="Export to PDF"
                >
                  <ArrowDownTrayIcon className="w-3 h-3" />
                  PDF
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-2 pt-2 border-t border-gray-100 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-gray-500 uppercase tracking-wider">Start Date:</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                  className="px-2 py-1 border border-gray-305 rounded-md bg-gray-50 focus:bg-white outline-none focus:ring-1 focus:ring-indigo-500 transition-all"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-gray-500 uppercase tracking-wider">End Date:</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                  className="px-2 py-1 border border-gray-305 rounded-md bg-gray-50 focus:bg-white outline-none focus:ring-1 focus:ring-indigo-500 transition-all"
                />
              </div>

              <button
                onClick={handleResetFilters}
                className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold text-gray-500 hover:text-indigo-600 hover:bg-gray-100 transition-all ml-auto"
              >
                <ArrowPathIcon className="h-3.5 w-3.5" />
                Reset Filters
              </button>
            </div>
          </div>

          <CommonListPagination
            sticky
            pagination={{ page, limit, total: totalCount, pages: totalPages }}
            entityLabel="activities"
            loading={loading}
            hasItems={logs.length > 0}
            onPageChange={setPage}
            onLimitChange={(nextLimit) => { setLimit(nextLimit); setPage(1); }}
            icon="users"
          />

          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <LoadingSpinner size="lg" />
                <span className="text-sm font-semibold text-gray-500 mt-3">Retrieving system activity...</span>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center mb-3">
                  <svg className="h-6 w-6 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h4 className="text-base font-bold text-gray-900">Failed to Load Logs</h4>
                <p className="text-sm text-gray-500 mt-1 max-w-md">{error}</p>
                <button
                  onClick={fetchAuditLogs}
                  className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-all shadow-sm"
                >
                  Retry Fetch
                </button>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-20 px-4">
                <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-3">
                  <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 8.293A1 1 0 013 7.586V4z" />
                  </svg>
                </div>
                <h4 className="text-base font-bold text-gray-900">No Logs Found</h4>
                <p className="text-sm text-gray-500 mt-1">Try refining search parameters or adjusting date ranges.</p>
              </div>
            ) : (
              <table className="min-w-max w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                    <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operator/Employee</th>
                    <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Method</th>
                    <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action Path</th>
                    <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                    <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Status</th>
                    <th className="px-4 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log) => {
                    const empId = log.employeeDetails?.employeeId;
                    const empName = log.employeeDetails?.name;
                    const userMail = log.userEmail || '—';

                    const methodColors = {
                      GET: 'bg-sky-50 text-sky-700 border border-sky-100',
                      POST: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
                      PUT: 'bg-blue-50 text-blue-700 border border-blue-100',
                      DELETE: 'bg-rose-50 text-rose-700 border border-rose-100',
                      PATCH: 'bg-purple-50 text-purple-700 border border-purple-100',
                    };

                    let statusColor = 'bg-gray-50 text-gray-705 border border-gray-200';
                    if (log.statusCode) {
                      if (log.statusCode < 300) {
                        statusColor = 'bg-green-50 text-green-700 border border-green-100';
                      } else if (log.statusCode < 400) {
                        statusColor = 'bg-blue-50 text-blue-700 border border-blue-100';
                      } else if (log.statusCode < 500) {
                        statusColor = 'bg-amber-50 text-amber-700 border border-amber-100';
                      } else {
                        statusColor = 'bg-rose-50 text-rose-700 border border-rose-100';
                      }
                    }

                    return (
                      <tr key={log._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-1 text-xs font-medium text-gray-500 whitespace-nowrap">
                          {formatTimestamp(log.createdAt)}
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-[9px] font-semibold text-indigo-700 flex-shrink-0">
                              {getInitials(empName || userMail)}
                            </div>
                            <div className="flex flex-col">
                              <span className="text-xs font-semibold text-gray-900 leading-tight">
                                {empName || 'External Operator'}
                              </span>
                              <span className="text-[10px] text-gray-500 leading-none flex items-center gap-1">
                                {userMail}
                                {empId && (
                                  <span className="inline-block px-1 py-0.2 bg-gray-100 text-[9px] font-bold text-gray-600 rounded border border-gray-200">
                                    {empId}
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-1 text-center whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${methodColors[log.method] || 'bg-gray-50 text-gray-600'}`}>
                            {log.method}
                          </span>
                        </td>
                        <td className="px-3 py-1 max-w-xs truncate text-xs font-mono text-gray-650" title={log.action || log.resource}>
                          {log.action || log.resource}
                        </td>
                        <td className="px-3 py-1 text-xs font-medium text-gray-600 whitespace-nowrap font-mono">
                          {log.ipAddress || '—'}
                        </td>
                        <td className="px-3 py-1 text-center whitespace-nowrap">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${statusColor}`}>
                            {log.statusCode || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-1 text-right whitespace-nowrap">
                          <button
                            onClick={() => setSelectedLog(log)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-[10px] font-semibold rounded shadow-sm hover:shadow transition-all"
                          >
                            Show
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {selectedLog && (
        <div className="fixed inset-0 overflow-hidden z-50">
          <div className="absolute inset-0 overflow-hidden">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-xs transition-opacity"
              onClick={() => setSelectedLog(null)}
            />

            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <div className="pointer-events-auto w-screen max-w-2xl transform transition-transform duration-300 ease-in-out">
                <div className="flex h-full flex-col bg-white shadow-2xl border-l border-gray-200">
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-white">
                        Audit Log Request Details
                      </h2>
                      <p className="text-xs text-white/80 mt-0.5">
                        ID: {selectedLog._id} | Method: {selectedLog.method}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedLog(null)}
                      className="rounded-md text-white/80 hover:text-white hover:bg-white/10 p-1.5 transition-all"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-6 space-y-6 text-sm">
                    {selectedLog.statusCode >= 500 && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-805 text-xs flex gap-2 items-start shadow-xs">
                        <svg className="w-4 h-4 text-red-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                          <p className="font-bold">System Error Alert: HTTP {selectedLog.statusCode}</p>
                          <p className="mt-0.5 opacity-90">An internal server error was thrown during this client request. Please review system mutation logs and exception payload details below.</p>
                        </div>
                      </div>
                    )}

                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200 p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                            Operator Profile
                          </span>
                          <p className="text-sm font-semibold text-gray-900 mt-1">
                            {selectedLog.employeeDetails?.name || 'External / System Operator'}
                          </p>
                        </div>
                        <div>
                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                            Operator Email
                          </span>
                          <p className="text-sm font-medium text-gray-900 mt-1 truncate">
                            {selectedLog.userEmail || '—'}
                          </p>
                        </div>
                        <div>
                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                            Executed At
                          </span>
                          <p className="text-sm font-semibold text-gray-900 mt-1">
                            {formatTimestamp(selectedLog.createdAt)}
                          </p>
                        </div>
                        <div>
                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                            IP Address
                          </span>
                          <div className="mt-1 flex items-center gap-1 font-mono text-sm text-gray-900">
                            <MapPinIcon className="h-4 w-4 text-indigo-500 shrink-0" />
                            {selectedLog.ipAddress || '—'}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                        Request Path URI
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-250 font-mono text-xs text-gray-705 flex flex-wrap gap-2 items-center">
                        <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded font-bold">{selectedLog.method}</span>
                        <span className="break-all">{selectedLog.action || selectedLog.resource}</span>
                        <span className="ml-auto flex items-center gap-1">
                          Status:
                          <span className={`font-bold font-mono ${selectedLog.statusCode >= 400 ? 'text-rose-600' : 'text-green-600'}`}>{selectedLog.statusCode}</span>
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                        <ComputerDesktopIcon className="h-3.5 w-3.5 text-indigo-500" />
                        Platform & Browser
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 font-semibold text-gray-805 text-xs">
                        {parseUserAgent(selectedLog.userAgent).browser} ({parseUserAgent(selectedLog.userAgent).os})
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                        <ClockIcon className="h-3.5 w-3.5 text-indigo-500" />
                        User Agent Details
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 font-mono text-[10px] text-gray-600 break-all max-h-[80px] overflow-y-auto">
                        {selectedLog.userAgent || '—'}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                          Request Data Payload
                        </h3>
                        {selectedLog.requestData && (
                          <button
                            onClick={() => handleCopyPayload(selectedLog.requestData)}
                            className="flex items-center gap-1 px-2.5 py-1 border border-gray-300 rounded bg-white text-xs font-semibold text-gray-650 hover:bg-gray-50 shadow-xs transition-all"
                          >
                            {copied ? (
                              <>
                                <CheckIcon className="h-3.5 w-3.5 text-green-600" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <ClipboardIcon className="h-3.5 w-3.5" />
                                Copy Payload
                              </>
                            )}
                          </button>
                        )}
                      </div>
                      <div className="bg-gray-950 rounded-lg p-4 border border-gray-800 text-gray-100 overflow-x-auto max-h-[300px]">
                        {selectedLog.requestData ? (
                          <pre className="font-mono text-xs leading-relaxed">
                            {JSON.stringify(selectedLog.requestData, null, 2)}
                          </pre>
                        ) : (
                          <span className="text-gray-500 font-medium italic text-xs">No request body payload was sent with this action.</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 px-6 py-4 flex justify-end bg-gray-50">
                    <button
                      onClick={() => setSelectedLog(null)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-all shadow-xs"
                    >
                      Close Audit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogsViewer;
